import { TgsArticleImage } from '~/common/components/ArticleImage';
import { cn } from '~/common/utils/styleUtil';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { ArticleSecondaryText } from '~/features/catalog/components/article/ArticleSecondaryText';
import { useArticleDetailPath } from '~/features/catalog/hooks/useArticleDetailPath';
import { AddToTemplate } from '~/features/catalog/components/AddToTemplate';
import type { AghArticle } from '~/common/types/aghArticle';
import { useCatalogAghArticle } from '~/features/catalog/hooks/useCatalogAghArticle';
import { CatalogAghArticleNotAvailable } from '~/features/catalog/components/CatalogAghArticleNotAvailable';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import ArticleAghInfoIcons from '~/features/catalog/components/ArticleAghInfoIcons.tsx';
import { Link } from 'react-router';
import React from 'react';

type CatalogListItemProps = {
  index: number;
  article: AghArticle;
  isAuthenticated: boolean;
  className?: string;
};

export function CatalogAghListItem({ article, isAuthenticated, className }: Readonly<CatalogListItemProps>) {
  const catalogArticle = useCatalogAghArticle(article);
  const articleDetailPath = useArticleDetailPath(catalogArticle.articleNumber);
  const { isXlDown } = useScreenSize();

  const handleInteractiveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <article
      className={cn(
        'group border-b desktop:border-b-0 border-r-0 p-4  pl-0 xl:pl-4 hover:bg-neutral-50 h-full flex flex-col',
        className
      )}
    >
      <Link
        to={articleDetailPath}
        state={{ quantity: catalogArticle.quantity }}
        id={catalogArticle.articleNumber}
        className="flex flex-row flex-nowrap gap-4 items-stretch h-full no-underline text-inherit"
        onClick={e => {
          const target = e.target as HTMLElement;
          if (target.closest('.interactive-element')) {
            e.preventDefault();
          }
        }}
      >
        <div className="flex">
          <TgsArticleImage
            articleNumber={catalogArticle.articleNumber}
            celumId={catalogArticle.celumId}
            alt={catalogArticle.description}
            size={isXlDown ? 'small' : 'normal'}
          />
        </div>
        <div className="grow min-w-0">
          <div className="flex flex-col justify-center h-full">
            <div
              className={cn(
                'text-base font-bold line-clamp-2',
                catalogArticle.isAction && !catalogArticle.isHit && !catalogArticle.isFutureAction && 'text-primary'
              )}
              data-testid={`article-list-description-${catalogArticle.articleNumber}`}
            >
              {catalogArticle.description}
            </div>
            <ArticleSecondaryText
              articleNumber={catalogArticle.articleNumber}
              rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
              brand={catalogArticle.brand}
              lineClamp={3}
            />
            <ArticleAghInfoIcons article={catalogArticle} className="flex py-1" isAuthenticated={isAuthenticated} />
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          className="grow-0 shrink-0 min-w-[350px] interactive-element cursor-default"
          onClick={handleInteractiveClick}
        >
          <div className="flex flex-row items-center justify-end h-full">
            <ArticlePrice
              showOnlyActionPrice={true}
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidFrom={catalogArticle.actionValidFrom}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              className="lg:mx-4 xl:mx-8"
              minimize={true}
              isHit={catalogArticle.isHit}
            />
            <div>
              <div>
                <ArticleSellUnit
                  showOnlyActionPrice={true}
                  selectedArticle={catalogArticle}
                  allArticles={catalogArticle.allArticles}
                  onArticleChange={changeInfo =>
                    catalogArticle.setSelectedArticleNumber(changeInfo.articleNumber.toString())
                  }
                />
              </div>
              <AddToTemplate
                className="min-w-[200px]"
                isAuthenticated={isAuthenticated}
                mainArticleId={catalogArticle.mainArticleId}
                articleNumber={catalogArticle.articleNumber}
                articleDescriptionText={catalogArticle.description}
                quantity={catalogArticle.quantity}
                onQuantityChange={catalogArticle.setQuantity}
                calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
                cacheQuantity={true}
                forceSmallAddButton={true}
              />
            </div>
          </div>
        </div>
      </Link>
      <CatalogAghArticleNotAvailable
        status={catalogArticle.status}
        showArticleNotAvailable={catalogArticle.showCurrentlyNotAvailableMessage}
        substituteArticleNumber={catalogArticle.substituteArticleNumber}
        substituteArticleText={catalogArticle.substituteArticleText}
      />
    </article>
  );
}
